import type { JsonObject, TrackingPrivacyMode } from './contracts';
import { getClickProperties, getFormProperties } from './page';
import { SectionEngagementTracker } from './section-engagement';

export type BrowserEventHandlers = {
  onActivity(type: string, properties?: JsonObject): void;
  onClickStart(event: MouseEvent): void;
  onEngagementChange(engaged: boolean): void;
  onFailure(error: unknown, operation: string): void;
  onFlush(preferBeacon: boolean): void;
  onPageView(): void;
  onSessionEnd(): void;
};

type Disposer = () => void;

const FORM_SUBMISSION_DEDUPLICATION_WINDOW_MS = 1_000;
const HASH_ROUTE_PREFIXES = ['#/', '#!/'] as const;

// A fragment identifies the page only for a hash router. Any other fragment is
// an anchor in the same page, such as an in-page link or a scroll spy.
const pageKey = ({ hash, origin, pathname, search }: Location): string => {
  const hashRoute = HASH_ROUTE_PREFIXES.some(prefix => hash.startsWith(prefix)) ? hash : '';
  return `${origin}${pathname}${search}${hashRoute}`;
};

const listen = <K extends keyof WindowEventMap>(
  target: Window,
  type: K,
  listener: (event: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions
): Disposer => {
  target.addEventListener(type, listener as EventListener, options);
  return () => target.removeEventListener(type, listener as EventListener, options);
};

const listenOnDocument = <K extends keyof DocumentEventMap>(
  type: K,
  listener: (event: DocumentEventMap[K]) => void,
  options?: AddEventListenerOptions
): Disposer => {
  document.addEventListener(type, listener as EventListener, options);
  return () => document.removeEventListener(type, listener as EventListener, options);
};

const installHistoryListener = (onNavigate: () => void): Disposer => {
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  const wrappedPushState: History['pushState'] = function (data, unused, url) {
    originalPushState.call(history, data, unused, url);
    onNavigate();
  };
  const wrappedReplaceState: History['replaceState'] = function (data, unused, url) {
    originalReplaceState.call(history, data, unused, url);
    onNavigate();
  };

  history.pushState = wrappedPushState;
  history.replaceState = wrappedReplaceState;

  return () => {
    if (history.pushState === wrappedPushState) history.pushState = originalPushState;
    if (history.replaceState === wrappedReplaceState) history.replaceState = originalReplaceState;
  };
};

export const installBrowserEvents = (
  handlers: BrowserEventHandlers,
  privacyMode: TrackingPrivacyMode
): Disposer => {
  const disposers: Disposer[] = [];
  let currentPageKey = pageKey(window.location);
  const sectionEngagement = new SectionEngagementTracker(
    {
      onActivity: (type, properties) => handlers.onActivity(type, properties),
      onFailure: (error, operation) => handlers.onFailure(error, operation)
    },
    document.visibilityState !== 'hidden' && document.hasFocus()
  );
  const scrollThresholds = new Set([25, 50, 75, 100]);
  const reachedScrollThresholds = new Set<number>();
  const recentFormSubmissions = new WeakMap<HTMLFormElement, number>();
  const safely = (operation: string, callback: () => void): void => {
    try {
      callback();
    } catch (error) {
      handlers.onFailure(error, operation);
    }
  };

  const handleNavigation = (): void => {
    safely('handle_navigation', () => {
      const nextPageKey = pageKey(window.location);
      if (nextPageKey === currentPageKey) return;
      currentPageKey = nextPageKey;
      reachedScrollThresholds.clear();
      sectionEngagement.resetForNavigation();
      handlers.onPageView();
    });
  };

  sectionEngagement.start();

  const captureFormSubmission = (
    form: HTMLFormElement,
    preferBeacon: boolean
  ): void => {
    const now = Date.now();
    const lastCapturedAt = recentFormSubmissions.get(form);
    if (
      lastCapturedAt !== undefined
      && now - lastCapturedAt < FORM_SUBMISSION_DEDUPLICATION_WINDOW_MS
    ) return;

    const properties = getFormProperties(form, privacyMode);
    if (!properties) return;
    recentFormSubmissions.set(form, now);
    handlers.onActivity('form_submit', properties);
    handlers.onFlush(preferBeacon);
  };

  disposers.push(installHistoryListener(handleNavigation));
  disposers.push(listen(window, 'popstate', handleNavigation));
  disposers.push(listen(window, 'hashchange', handleNavigation));

  // Window capture runs before document and target handlers, including handlers installed before the SDK.
  for (const type of ['click', 'auxclick'] as const) {
    disposers.push(listen(window, type, event => {
      safely('capture_interaction', () => handlers.onClickStart(event));
    }, { capture: true, passive: true }));
  }

  disposers.push(listenOnDocument('click', (event) => {
    safely('capture_click', () => {
      if (!(event.target instanceof Element)) return;
      const properties = getClickProperties(event.target, event, privacyMode);
      if (properties) handlers.onActivity('click', properties);
    });
  }, { capture: true, passive: true }));

  disposers.push(listenOnDocument('auxclick', event => {
    if (event.button !== 1) return;
    safely('capture_click', () => {
      if (!(event.target instanceof Element)) return;
      const properties = getClickProperties(event.target, event, privacyMode);
      if (properties) handlers.onActivity('click', properties);
    });
  }, { capture: true, passive: true }));

  disposers.push(listen(window, 'submit', (event) => {
    safely('capture_form_submit', () => {
      if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      if (!form.checkValidity()) return;
      captureFormSubmission(form, false);
    });
  }));

  safely('install_programmatic_form_submit', () => {
    const originalSubmit = HTMLFormElement.prototype.submit;
    const wrappedSubmit: HTMLFormElement['submit'] = function (this: HTMLFormElement) {
      safely('capture_programmatic_form_submit', () => {
        captureFormSubmission(this, true);
      });
      originalSubmit.call(this);
    };

    HTMLFormElement.prototype.submit = wrappedSubmit;
    disposers.push(() => {
      if (HTMLFormElement.prototype.submit === wrappedSubmit) {
        HTMLFormElement.prototype.submit = originalSubmit;
      }
    });
  });

  const handleScroll = (): void => {
    safely('capture_scroll', () => {
      const documentHeight = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight ?? 0);
      const scrollableHeight = documentHeight - window.innerHeight;
      const depth = scrollableHeight <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));

      for (const threshold of scrollThresholds) {
        if (depth >= threshold && !reachedScrollThresholds.has(threshold)) {
          reachedScrollThresholds.add(threshold);
          handlers.onActivity('scroll_depth', { percent: threshold });
        }
      }
    });
  };
  disposers.push(listen(window, 'scroll', handleScroll, { passive: true }));

  disposers.push(listen(window, 'focus', () => {
    safely('capture_focus', () => {
      if (document.visibilityState !== 'hidden') {
        sectionEngagement.setDocumentEngaged(true);
        handlers.onEngagementChange(true);
      }
    });
  }));

  disposers.push(listen(window, 'blur', () => {
    safely('capture_blur', () => {
      sectionEngagement.setDocumentEngaged(false);
      handlers.onEngagementChange(false);
    });
  }));

  disposers.push(listen(window, 'pagehide', () => {
    safely('capture_pagehide', () => {
      sectionEngagement.stop('pagehide');
      handlers.onSessionEnd();
      handlers.onFlush(true);
    });
  }));

  disposers.push(listenOnDocument('visibilitychange', () => {
    safely('capture_visibility', () => {
      const visible = document.visibilityState !== 'hidden';
      sectionEngagement.setDocumentEngaged(visible && document.hasFocus());
      handlers.onEngagementChange(visible && document.hasFocus());
      if (!visible) handlers.onFlush(true);
    });
  }));

  return () => {
    sectionEngagement.stop('stop');
    for (const dispose of disposers.reverse()) dispose();
  };
};
